import ExcelJS from 'exceljs';
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';

export interface WorkbookSheetSnapshot {
  readonly name: string;
  readonly rows: readonly (readonly unknown[])[];
  readonly rowCount: number;
  readonly columnCount: number;
}

export interface ExcelWorkbookReadOptions {
  readonly maxRowsToRead: number;
  readonly maxColumnsToRead: number;
}

export interface ExcelWorkbookReader {
  read(
    arrayBuffer: ArrayBuffer,
    options: ExcelWorkbookReadOptions
  ): Promise<readonly WorkbookSheetSnapshot[]>;
}

export class ExcelJsWorkbookReader implements ExcelWorkbookReader {
  async read(
    arrayBuffer: ArrayBuffer,
    options: ExcelWorkbookReadOptions
  ): Promise<readonly WorkbookSheetSnapshot[]> {
    const workbook = new ExcelJS.Workbook();
    await loadWorkbookForImport(workbook, arrayBuffer);

    return workbook.worksheets.map(worksheet => {
      const rowCount = worksheet.rowCount;
      const columnCount = worksheet.columnCount;
      const rowsToRead = Math.min(rowCount, options.maxRowsToRead);
      const columnsToRead = Math.min(columnCount, options.maxColumnsToRead);
      const rows: unknown[][] = [];

      for (let rowIndex = 1; rowIndex <= rowsToRead; rowIndex += 1) {
        const row = worksheet.getRow(rowIndex);
        const values: unknown[] = [];
        let hasValue = false;

        for (
          let columnIndex = 1;
          columnIndex <= columnsToRead;
          columnIndex += 1
        ) {
          const value = normalizeCellValue(row.getCell(columnIndex).value);
          values.push(value);
          if (value != null && String(value).trim() !== '') {
            hasValue = true;
          }
        }

        if (hasValue) {
          rows.push(values);
        }
      }

      return {
        name: worksheet.name,
        rows,
        rowCount,
        columnCount,
      };
    });
  }
}

export const excelWorkbookReader: ExcelWorkbookReader =
  new ExcelJsWorkbookReader();

const EXCEL_IMPORT_READ_OPTIONS = {
  ignoreNodes: ['picture', 'drawing', 'tableParts'],
} satisfies Partial<ExcelJS.XlsxReadOptions>;

async function loadWorkbookForImport(
  workbook: ExcelJS.Workbook,
  arrayBuffer: ArrayBuffer
): Promise<void> {
  try {
    await workbook.xlsx.load(arrayBuffer, EXCEL_IMPORT_READ_OPTIONS);
  } catch (error) {
    if (isUnsupportedSpreadsheetNamespaceError(error)) {
      const normalizedWorkbook =
        normalizeSpreadsheetMlNamespacePrefixes(arrayBuffer);
      try {
        await workbook.xlsx.load(normalizedWorkbook, EXCEL_IMPORT_READ_OPTIONS);
        return;
      } catch (normalizedError) {
        if (!isUnsupportedDrawingWorkbookError(normalizedError)) {
          throw normalizedError;
        }
        await workbook.xlsx.load(
          stripUnsupportedDrawingParts(normalizedWorkbook),
          EXCEL_IMPORT_READ_OPTIONS
        );
        return;
      }
    }

    if (!isUnsupportedDrawingWorkbookError(error)) throw error;
    await workbook.xlsx.load(
      stripUnsupportedDrawingParts(arrayBuffer),
      EXCEL_IMPORT_READ_OPTIONS
    );
  }
}

function isUnsupportedSpreadsheetNamespaceError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    /Cannot read properties of undefined \(reading ['"]sheets['"]\)/.test(
      error.message
    )
  );
}

function isUnsupportedDrawingWorkbookError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    /Cannot read properties of undefined \(reading ['"]anchors['"]\)/.test(
      error.message
    )
  );
}

function normalizeSpreadsheetMlNamespacePrefixes(
  arrayBuffer: ArrayBuffer
): ArrayBuffer {
  const sourceEntries = unzipSync(new Uint8Array(arrayBuffer));
  const targetEntries: Record<string, Uint8Array> = {};

  for (const [name, data] of Object.entries(sourceEntries)) {
    if (!name.endsWith('.xml')) {
      targetEntries[name] = data;
      continue;
    }

    const xml = strFromU8(data);
    if (
      !xml.includes(
        'xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"'
      )
    ) {
      targetEntries[name] = data;
      continue;
    }

    targetEntries[name] = strToU8(
      xml
        .replace(
          'xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"',
          'xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"'
        )
        .replace(/<(\/?)x:/g, '<$1')
    );
  }

  return zipEntriesToArrayBuffer(targetEntries);
}

function stripUnsupportedDrawingParts(arrayBuffer: ArrayBuffer): ArrayBuffer {
  const sourceEntries = unzipSync(new Uint8Array(arrayBuffer));
  const targetEntries: Record<string, Uint8Array> = {};

  for (const [name, data] of Object.entries(sourceEntries)) {
    if (name.startsWith('xl/drawings/') || name.startsWith('xl/charts/')) {
      continue;
    }

    if (/^xl\/worksheets\/sheet\d+\.xml$/.test(name)) {
      targetEntries[name] = strToU8(
        stripWorksheetDrawingNodes(strFromU8(data))
      );
      continue;
    }

    if (/^xl\/worksheets\/_rels\/sheet\d+\.xml\.rels$/.test(name)) {
      targetEntries[name] = strToU8(
        stripWorksheetDrawingRelationships(strFromU8(data))
      );
      continue;
    }

    if (name === '[Content_Types].xml') {
      targetEntries[name] = strToU8(stripDrawingContentTypes(strFromU8(data)));
      continue;
    }

    targetEntries[name] = data;
  }

  return zipEntriesToArrayBuffer(targetEntries);
}

function zipEntriesToArrayBuffer(
  entries: Record<string, Uint8Array>
): ArrayBuffer {
  const archive = zipSync(entries);
  const buffer = new ArrayBuffer(archive.byteLength);
  new Uint8Array(buffer).set(archive);
  return buffer;
}

function stripWorksheetDrawingNodes(xml: string): string {
  return xml
    .replace(/<drawing\b[^>]*>\s*<\/drawing>/g, '')
    .replace(/<drawing\b[^>]*\/>/g, '')
    .replace(/<legacyDrawing\b[^>]*>\s*<\/legacyDrawing>/g, '')
    .replace(/<legacyDrawing\b[^>]*\/>/g, '')
    .replace(/<legacyDrawingHF\b[^>]*>\s*<\/legacyDrawingHF>/g, '')
    .replace(/<legacyDrawingHF\b[^>]*\/>/g, '')
    .replace(/<picture\b[^>]*>\s*<\/picture>/g, '')
    .replace(/<picture\b[^>]*\/>/g, '');
}

function stripWorksheetDrawingRelationships(xml: string): string {
  return xml.replace(
    /<Relationship\b(?=[^>]*Type="[^"]*\/drawing")(?=[^>]*Target="[^"]*drawings\/)[^>]*\/>/g,
    ''
  );
}

function stripDrawingContentTypes(xml: string): string {
  return xml.replace(
    /<Override\b(?=[^>]*PartName="\/xl\/(?:drawings|charts)\/)[^>]*\/>/g,
    ''
  );
}

function normalizeCellValue(value: ExcelJS.CellValue): unknown {
  if (value == null) {
    return '';
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  ) {
    return value;
  }
  if (isFormulaValue(value)) {
    return normalizeCellValue(value.result ?? '');
  }
  if (isRichTextValue(value)) {
    return value.richText.map(part => part.text).join('');
  }
  if (isHyperlinkValue(value)) {
    return value.text || value.hyperlink;
  }

  return String(value);
}

function isFormulaValue(
  value: ExcelJS.CellValue
): value is ExcelJS.CellFormulaValue {
  return typeof value === 'object' && value !== null && 'formula' in value;
}

function isRichTextValue(
  value: ExcelJS.CellValue
): value is ExcelJS.CellRichTextValue {
  return typeof value === 'object' && value !== null && 'richText' in value;
}

function isHyperlinkValue(
  value: ExcelJS.CellValue
): value is ExcelJS.CellHyperlinkValue {
  return typeof value === 'object' && value !== null && 'hyperlink' in value;
}
